import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { useSearchParams, useNavigate } from "react-router-dom";
import { API_URL } from "../api";
import { getImageUrl } from "../utils/imageUrl";
import Login from "../pages/Login"; 
import MobileBottomNav from "../mobile/MobileBottomNav";
import { COLOR, FONT, RADIUS, SHADOW, GlobalMobileStyles, ThemeProvider } from "../mobile/mobiletheme";

const GRADIENT = "linear-gradient(135deg, #1E90FF 0%, #1E90FF 100%)";
const LOW_STOCK_THRESHOLD = 5;

export default function MobileSearchResults() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const getName = (product) => {
    let name = product?.name;
    if (typeof name === "string") { try { const parsed = JSON.parse(name); if (parsed && typeof parsed === "object") name = parsed; } catch (_) {} }
    if (typeof name === "string") return name;
    const lang = (i18n.language || "en").split("-")[0];
    return name?.[lang] || name?.en || name?.hi || name?.te || "";
  };
  
  const query = searchParams.get("q") || "";
  const [search, setSearch] = useState(query);
  const searchInputRef = useRef(null);
  const [items, setItems] = useState([]);
  const [cartItems, setCartItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const user_id = localStorage.getItem("phone") || "guest";

  const [showVoiceModal, setShowVoiceModal] = useState(false);
  const [transcript, setTranscript] = useState(t("header.listening"));
  const [isListening, setIsListening] = useState(false);
  
  const [showLogin, setShowLogin] = useState(false);
  const isLoggedIn = localStorage.getItem("isLoggedIn") === "true";

  const cartFetchSeq = useRef(0);

  const fetchCartOnly = async () => {
    if (!isLoggedIn) return;
    const seq = ++cartFetchSeq.current;
    try {
      const cartRes = await axios.get(`${API_URL}/cart/${user_id}`);
      if (seq !== cartFetchSeq.current) return;
      if (cartRes.data.success) setCartItems(cartRes.data.cart || []);
    } catch (err) {
      console.error("Cart fetch error:", err);
    }
  };

  const fetchData = async (showLoading = true) => {
    if (showLoading) {
      setIsLoading(true);
      setError(false);
    }
    try {
      const res = await axios.get(`${API_URL}/search?q=${encodeURIComponent(query)}`);
      if (res.data.success) {
        setItems(res.data.items || []);
      } else if (showLoading) {
        setError(true);
      }

      if (isLoggedIn) {
        const cartRes = await axios.get(`${API_URL}/cart/${user_id}`);
        if (cartRes.data.success) setCartItems(cartRes.data.cart || []);
      }
    } catch (err) {
      console.error("Error fetching data:", err);
      if (showLoading) setError(true);
    } finally {
      if (showLoading) setIsLoading(false);
    }
  };

  useEffect(() => {
    if (searchInputRef.current && searchInputRef.current.value !== query) {
      searchInputRef.current.value = query;
    }
    setSearch(query);
    if (query) fetchData(true);
  }, [query]);

  const goSearch = (event) => {
    if (event?.preventDefault) event.preventDefault();
    const currentSearch = searchInputRef.current?.value ?? search;
    let cleanText = currentSearch.trim().toLowerCase();
    const synonymDictionary = {
      "bangaladumpa": "potato", "bangala dumpa": "potato",
      "ulli": "onion", "ullipaya": "onion", "errapaya": "onion",
      "tamata": "tomato", "tomatoo": "tomato",
      "bendakaya": "okra", "benda kaya": "okra", "lady finger": "okra",
      "mirapakaya": "chilli", "pachi mirchi": "chilli", "mirchi": "chilli",
      "kothimeera": "coriander", "dhaniya": "coriander",
      "karivepaku": "curry leaves",
      "vankaya": "brinjal", "eggplant": "brinjal",
      "pudina": "mint",
      "kanda": "yam",
      "kyabeji": "cabbage"
    };
    if (synonymDictionary[cleanText]) {
      cleanText = synonymDictionary[cleanText];
    }
    if (cleanText !== "") navigate(`/search?q=${encodeURIComponent(cleanText)}`);
  };

  const handleVoiceSearch = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      alert(t("header.voiceNotSupported"));
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.lang = "en-IN"; 
    recognition.interimResults = true; 
    recognition.maxAlternatives = 1;

    setShowVoiceModal(true);
    setTranscript(t("header.listening"));
    setIsListening(true);

    recognition.onresult = (event) => {
      let interimTranscript = "";
      let finalTranscript = "";

      for (let i = event.resultIndex; i < event.results.length; ++i) {
        if (event.results[i].isFinal) {
          finalTranscript += event.results[i][0].transcript;
        } else {
          interimTranscript += event.results[i][0].transcript;
        }
      }

      setTranscript(finalTranscript || interimTranscript);

      if (finalTranscript) {
        let cleanText = finalTranscript.replace(/\.$/, "").trim().toLowerCase();
        
        const synonymDictionary = {
          "bangaladumpa": "potato", "bangala dumpa": "potato",
          "ulli": "onion", "ullipaya": "onion", "errapaya": "onion",
          "tamata": "tomato", "tomatoo": "tomato",
          "bendakaya": "okra", "benda kaya": "okra", "lady finger": "okra",
          "mirapakaya": "chilli", "pachi mirchi": "chilli", "mirchi": "chilli",
          "kothimeera": "coriander", "dhaniya": "coriander",
          "karivepaku": "curry leaves",
          "vankaya": "brinjal", "eggplant": "brinjal",
          "pudina": "mint",
          "kanda": "yam",
          "kyabeji": "cabbage"
        };

        if (synonymDictionary[cleanText]) {
          cleanText = synonymDictionary[cleanText];
        }

        setIsListening(false);
        setSearch(cleanText);
        if (searchInputRef.current) searchInputRef.current.value = cleanText;

        setTimeout(() => {
          setShowVoiceModal(false);
          navigate(`/search?q=${encodeURIComponent(cleanText)}`);
        }, 600);
      }
    };

    recognition.onerror = (event) => {
      console.error("Speech recognition error", event.error);
      if (event.error === "no-speech") {
        setTranscript(t("header.noSpeech"));
      } else {
        setTranscript(t("header.micError"));
      }
      setIsListening(false);
      setTimeout(() => setShowVoiceModal(false), 2500);
    };

    recognition.onend = () => setIsListening(false);
    recognition.start();
  };

  const closeVoiceModal = () => {
    setShowVoiceModal(false);
    setIsListening(false);
  };

  const SearchIcon = ({ size = 16, color = COLOR.muted }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="7" />
      <line x1="21" y1="21" x2="16" y2="16" />
      <circle cx="8.5" cy="8.5" r="1.8" fill={color} stroke="none" />
    </svg>
  );

  const MicIcon = ({ size = 20 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="7.5" y="1" width="9" height="14" rx="4.5" fill={COLOR.primary} />
      <circle cx="12" cy="6" r="1.5" fill={COLOR.ink} />
      <path d="M7.5 9.5 L12 12 L16.5 9.5" stroke={COLOR.onPrimary} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      <path d="M5 11v2a7 7 0 0 0 14 0v-2M12 20v3M8 23h8" stroke={COLOR.ink} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    </svg>
  );

  const [pendingIds, setPendingIds] = useState({});

  const addToCart = async (product) => {
    if (!isLoggedIn) {
      setShowLogin(true);
      return;
    }
    const pendingKey = `add-${product.id}`;
    if (pendingIds[pendingKey]) return;
    setPendingIds((p) => ({ ...p, [pendingKey]: true }));

    const tempId = `temp-${product.id}`;
    setCartItems((prev) => [...prev, { id: tempId, product_id: product.id, category: product.category, quantity: 1 }]);
    window.dispatchEvent(new Event("cartUpdated"));

    try {
      await axios.post(`${API_URL}/cart/add`, {
        user_id, product_id: product.id, category: product.category,
      });
      await fetchCartOnly();
    } catch (err) {
      console.error(err);
      setCartItems((prev) => prev.filter((i) => i.id !== tempId));
    } finally {
      setPendingIds((p) => {
        const next = { ...p };
        delete next[pendingKey];
        return next;
      });
    }
  };

  const updateQty = async (cart_id, action) => {
    if (typeof cart_id === "string" && cart_id.startsWith("temp-")) return; 
    if (pendingIds[cart_id]) return;
    const item = cartItems.find((i) => i.id === cart_id);
    if (!item) return;
    const prevSnapshot = cartItems;
    const willRemove = action === "decrease" && item.quantity <= 1;
    setPendingIds((p) => ({ ...p, [cart_id]: true }));

    setCartItems((prev) =>
      willRemove
        ? prev.filter((i) => i.id !== cart_id)
        : prev.map((i) => (i.id === cart_id ? { ...i, quantity: action === "increase" ? i.quantity + 1 : i.quantity - 1 } : i))
    );
    window.dispatchEvent(new Event("cartUpdated"));

    try {
      if (willRemove) {
        await axios.delete(`${API_URL}/cart/${cart_id}`);
      } else {
        await axios.put(`${API_URL}/cart/${action}/${cart_id}`);
      }
      await fetchCartOnly();
    } catch (err) {
      console.error(err);
      setCartItems(prevSnapshot);
    } finally {
      setPendingIds((p) => {
        const next = { ...p };
        delete next[cart_id];
        return next;
      });
    }
  };

  return (
    <ThemeProvider>
      <GlobalMobileStyles />
      <style>
        {`
          .mobile-search-input::-webkit-search-cancel-button { -webkit-appearance: none; appearance: none; }
          .mobile-search-input::-webkit-search-decoration { -webkit-appearance: none; }
          .mobile-search-input::placeholder { color: ${COLOR.placeholder}; opacity: 1; }
          .mobile-search-input:focus { outline: none !important; color: ${COLOR.ink} !important; caret-color: ${COLOR.primaryDark} !important; }
          @keyframes pulseGlow {
            0% { box-shadow: 0 0 0 0 rgba(30, 144, 255, 0.4); }
            70% { box-shadow: 0 0 0 20px rgba(30, 144, 255, 0); }
            100% { box-shadow: 0 0 0 0 rgba(30, 144, 255, 0); }
          }
        `}
      </style>

      {showLogin && (
        <div style={styles.overlay}>
          <div style={styles.loginBox}>
            <button style={styles.closeBtn} onClick={() => setShowLogin(false)}>×</button>
            <Login onLoginSuccess={() => { 
              setShowLogin(false); 
              window.location.reload(); 
            }} />
          </div>
        </div>
      )}

      {showVoiceModal && (
        <div style={styles.voiceOverlay} onClick={closeVoiceModal}>
          <div style={styles.voiceModal} onClick={(e) => e.stopPropagation()}>
            <button style={styles.closeModalBtn} onClick={closeVoiceModal}>✕</button>
            <h3 style={styles.voiceTitle}>{isListening ? t("header.speakNow") : t("header.processing")}</h3>
            <p style={{ ...styles.voiceTranscript, color: transcript === t("header.listening") ? COLOR.muted : COLOR.ink }}>
              {transcript}
            </p>
            <div style={{
              ...styles.bigMicContainer,
              animation: isListening ? "pulseGlow 1.5s infinite" : "none",
              background: isListening ? COLOR.primary : COLOR.line
            }}>
              <MicIcon size={34} />
            </div>
          </div>
        </div>
      )}

      <div style={styles.appContainer}>
        <div style={styles.staticHeader}>
          <form style={styles.headerTop} onSubmit={(e) => { e.preventDefault(); goSearch(); }} autoComplete="off">
            <div style={styles.searchRow}>
              <div style={styles.searchBox}>
                <div style={styles.searchIconWrapper}>
                  <SearchIcon size={17} />
                </div>
                <input
                  type="text"
                  inputMode="search"
                  autoComplete="off"
                  autoCorrect="off"
                  spellCheck="false"
                  placeholder={t("mobile.search", { defaultValue: "Search products" })}
                  ref={searchInputRef}
                  style={styles.searchInput}
                  defaultValue={query}
                  onInput={(e) => setSearch(e.currentTarget.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      goSearch();
                    }
                  }}
                />
                {search && (
                  <button
                    type="button"
                    aria-label="Clear search"
                    style={styles.clearBtn}
                    onClick={() => {
                      setSearch("");
                      if (searchInputRef.current) {
                        searchInputRef.current.value = "";
                        searchInputRef.current.focus();
                      }
                    }}
                  >×</button>
                )}
              </div>
              <button type="button" style={styles.micBtn} onClick={handleVoiceSearch} aria-label="Voice search">
                <MicIcon size={20} />
              </button>
            </div>
          </form>

          <div style={styles.navRow}>
            <button style={styles.backBtn} onClick={() => navigate(-1)}>←</button>
            <h2 style={styles.title}>{query ? t("mobile.resultsFor", { query }) : t("mobile.search")}</h2>
          </div>
        </div>

        <div style={styles.scrollArea}>
          {isLoading ? (
            <div style={styles.centerPrompt}> {t("mobile.loading")} </div>
          ) : error ? (
            <div style={styles.centerPrompt}>
              <p> {t("mobile.couldntLoadResults")} </p>
              <button style={styles.retryBtn} onClick={() => fetchData(true)}> {t("mobile.retry")} </button>
            </div>
          ) : items.length === 0 ? (
            <div style={styles.centerPrompt}> {t("mobile.noProducts")} </div>
          ) : (
            <div style={styles.blinkitGrid}>
              {items.map((product) => {
                const cartItem = cartItems.find((c) => c.product_id === product.id && c.category === product.category);
                const stockNum = Number(product.stock);
                const outOfStock = stockNum <= 0;
                const lowStock = !outOfStock && stockNum <= LOW_STOCK_THRESHOLD;
                const atStockLimit = !!cartItem && stockNum > 0 && cartItem.quantity >= stockNum;
                const addPending = !!pendingIds[`add-${product.id}`];
                
                return (
                  <div key={`${product.category}-${product.id}`} style={outOfStock ? { ...styles.categoryCard, ...styles.cardOutOfStock } : styles.categoryCard}>
                    
                    <div style={styles.imgWrapper}>
                      <img
                        src={getImageUrl(product.image)}
                        style={outOfStock ? { ...styles.image, ...styles.imageOutOfStock } : styles.image}
                        alt={getName(product)}
                      />
                      {outOfStock && <div style={styles.outOfStockBadge}> {t("mobile.outOfStock")} </div>}
                      {lowStock && <div style={styles.lowStockBadge}>{stockNum === 1 ? t("mobile.left", { count: 1 }) : t("mobile.left", { count: stockNum })}</div>}
                    </div>
                    
                    <h4 style={styles.catText}>
                      {getName(product)}
                      <span style={styles.unitText}>
                        {product.quantity || product.unit || "1 kg"}
                      </span>
                    </h4>

                    <div style={styles.bottomRow}>
                      <p style={styles.price}>₹{product.price}</p>
                      {outOfStock ? (
                        <span style={styles.outOfStockText}> {t("mobile.outOfStock")} </span>
                      ) : cartItem && isLoggedIn ? (
                        <div style={styles.qtyBox}>
                          <button style={styles.btn} disabled={!!pendingIds[cartItem.id] || addPending} onClick={() => updateQty(cartItem.id, "decrease")}>-</button>
                          <span style={styles.qty}>{cartItem.quantity}</span>
                          <button
                            style={{ ...styles.btn, ...((atStockLimit || addPending) ? styles.btnDisabled : {}) }}
                            disabled={!!pendingIds[cartItem.id] || addPending || atStockLimit}
                            title={atStockLimit ? t("mobile.onlyInStock", { count: stockNum }) : undefined}
                            onClick={() => updateQty(cartItem.id, "increase")}
                          >+</button>
                        </div>
                      ) : (
                        <button style={styles.addBtn} className="sb-dark-add-btn sb-tap" disabled={!!pendingIds[`add-${product.id}`]} onClick={() => addToCart(product)}>
                          {pendingIds[`add-${product.id}`] ? "..." : t("mobile.add")}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <MobileBottomNav />
      </div>
    </ThemeProvider>
  );
}


// Search input is intentionally isolated from global input styles used elsewhere in the app.
const styles = {
  appContainer: { backgroundColor: COLOR.mist, minHeight: "100vh", minHeight: "100dvh", position: "relative", overflowX: "hidden" },
  staticHeader: { position: "relative", width: "100%", zIndex: 10, boxShadow: SHADOW.soft, backgroundColor: COLOR.headerSurface, isolation: "isolate" },
  headerTop: { display: "flex", alignItems: "center", gap: "10px", padding: "12px 14px 10px", background: COLOR.headerSurface, color: COLOR.headerText, margin: 0 },
  
  searchRow: { display: "flex", alignItems: "center", width: "100%", minWidth: 0, gap: "8px", margin: 0 },
  searchBox: { flex: 1, minWidth: 0, height: "44px", display: "flex", alignItems: "center", backgroundColor: COLOR.paper, borderRadius: "13px", overflow: "hidden", boxShadow: SHADOW.rest, border: `1px solid ${COLOR.line}` },
  searchIconWrapper: { paddingLeft: "12px", display: "flex", alignItems: "center", justifyContent: "center" },
  searchInput: { flex: 1, minWidth: 0, height: "100%", border: "none", padding: "0 8px", outline: "none", fontSize: "14px", background: "transparent", color: COLOR.ink, caretColor: COLOR.primaryDark, WebkitAppearance: "none", appearance: "none", pointerEvents: "auto", userSelect: "text", WebkitUserSelect: "text", touchAction: "manipulation" },
  clearBtn: { width: "30px", height: "30px", marginRight: "4px", border: "none", borderRadius: "50%", background: COLOR.mist, color: COLOR.muted, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "20px", lineHeight: 1, cursor: "pointer", flexShrink: 0 },
  micBtn: { width: "44px", height: "44px", border: `1px solid ${COLOR.line}`, borderRadius: "13px", backgroundColor: COLOR.paper, color: COLOR.primaryDark, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0, boxShadow: SHADOW.rest },
  
  navRow: { display: "flex", alignItems: "center", height: "50px", padding: "0 14px", backgroundColor: COLOR.headerSurface, borderBottom: `1px solid ${COLOR.line}`, boxSizing: "border-box" },
  backBtn: { display: "flex", alignItems: "center", border: "none", background: "none", fontSize: "28px", fontWeight: "bold", cursor: "pointer", padding: 0, marginRight: "16px", color: COLOR.headerText },
  title: { fontSize: "16px", fontWeight: "800", margin: 0, color: COLOR.headerText, letterSpacing: "0.2px" },
  
  scrollArea: { paddingTop: "4px", paddingBottom: "calc(110px + env(safe-area-inset-bottom, 0px))", boxSizing: "border-box", minHeight: "calc(100dvh - 106px)" },
  blinkitGrid: { display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "12px", padding: "12px 12px 24px" },
  categoryCard: { backgroundColor: COLOR.paper, borderRadius: "16px", padding: "10px", display: "flex", flexDirection: "column", boxShadow: SHADOW.rest, border: `1px solid ${COLOR.line}`, minWidth: 0, overflow: "hidden", transition: "transform .18s ease, box-shadow .18s ease" },
  
  imgWrapper: { width: "100%", aspectRatio: "1 / 0.92", minHeight: "106px", maxHeight: "126px", display: "flex", justifyContent: "center", alignItems: "center", overflow: "hidden", marginBottom: "9px", backgroundColor: COLOR.mist, borderRadius: "11px", position: "relative" },
  image: { width: "100%", height: "100%", objectFit: "cover", display: "block" },
  cardOutOfStock: { opacity: 0.6 },
  imageOutOfStock: { filter: "grayscale(1)" },
  outOfStockBadge: { position: "absolute", top: "6px", left: "6px", backgroundColor: "rgba(0,0,0,0.75)", color: "#fff", fontSize: "9px", fontWeight: "bold", padding: "3px 6px", borderRadius: "5px", textTransform: "uppercase", letterSpacing: "0.2px" },
  outOfStockText: { fontSize: "12px", fontWeight: "bold", color: COLOR.error },
  lowStockBadge: { position: "absolute", top: "6px", left: "6px", backgroundColor: COLOR.pop, color: "#fff", fontSize: "9px", fontWeight: "bold", padding: "3px 6px", borderRadius: "5px", letterSpacing: "0.2px" },
  btnDisabled: { opacity: 0.4, cursor: "not-allowed" },
  
  catText: { margin: "0 0 9px 0", fontSize: "12.5px", fontWeight: "750", lineHeight: "1.28", minHeight: "46px", overflow: "hidden", color: COLOR.ink },
  unitText: { display: "block", fontSize: "11px", color: COLOR.muted, fontWeight: "normal", marginTop: "2px" },
  
  bottomRow: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: "7px", marginTop: "auto", minHeight: "32px" },
  price: { fontWeight: "850", fontSize: "14px", margin: 0, color: COLOR.ink, whiteSpace: "nowrap" },
  addBtn: { padding: "0 12px", border: `1.5px solid ${COLOR.primary}`, backgroundColor: COLOR.paper, color: COLOR.primary, borderRadius: "9px", minWidth: "58px", height: "32px", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: "800", fontSize: "11px", letterSpacing: "0.02em", textTransform: "uppercase", cursor: "pointer", flexShrink: 0 },
  qtyBox: { display: "flex", alignItems: "center", gap: "6px", background: COLOR.primary, border: `1.5px solid ${COLOR.primary}`, borderRadius: "10px", padding: "3px 6px", minWidth: "76px", justifyContent: "space-between", boxSizing: "border-box" },
  btn: { border: "none", background: "none", color: COLOR.onPrimary, fontWeight: "bold", cursor: "pointer", fontSize: "16px" },
  qty: { fontSize: "14px", fontWeight: "bold", color: COLOR.onPrimary },
  centerPrompt: { textAlign: "center", padding: "40px 20px", color: COLOR.muted },
  retryBtn: { marginTop: "10px", padding: "8px 20px", border: "none", borderRadius: "8px", background: COLOR.headerSurface, color: COLOR.onPrimary, fontWeight: "bold", cursor: "pointer" },

  overlay: { position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2000 },
  loginBox: { background: COLOR.paper, padding: "20px", borderRadius: "14px", width: "90%", position: "relative" },
  closeBtn: { position: "absolute", top: "10px", right: "10px", border: "none", background: "none", fontSize: "24px", cursor: "pointer", color: COLOR.ink, zIndex: 10 },

  voiceOverlay: { position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(0, 0, 0, 0.7)", zIndex: 9999, display: "flex", alignItems: "flex-end", justifyContent: "center" },
  voiceModal: { width: "100%", backgroundColor: COLOR.paper, borderTopLeftRadius: "24px", borderTopRightRadius: "24px", padding: "30px 20px 50px 20px", display: "flex", flexDirection: "column", alignItems: "center", position: "relative", boxShadow: SHADOW.float },
  closeModalBtn: { position: "absolute", top: "15px", right: "20px", background: "none", border: "none", fontSize: "20px", color: COLOR.muted, cursor: "pointer", padding: "5px" },
  voiceTitle: { margin: "0 0 15px 0", fontSize: "18px", fontWeight: "bold", color: COLOR.ink },
  voiceTranscript: { fontSize: "22px", textAlign: "center", minHeight: "60px", margin: "0 0 30px 0", display: "flex", alignItems: "center", fontStyle: "italic", maxWidth: "85%", color: COLOR.ink },
  bigMicContainer: { width: "70px", height: "70px", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", transition: "background 0.3s ease" }
};